import React, { useState, useEffect, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import _ from 'lodash'
import queryString from 'query-string'
import moment from 'moment'
import {
  Axios,
  Grid,
  Segment,
  DataTable,
  Table,
  Checkbox,
  ConditionWrapper,
  BaseForm as Form,
  Text,
  Button,
  notice,
  Util,
} from 'component/base'
import { Custom } from 'component/pieces'
import { saveAs } from 'file-saver'
import { getServerlessEndpoint } from 'config'

export const SeikyuShiharaiMeisai = withRouter(props => {
  const {
    history,
    enableQueryCondition,
    multiple,
    selectedId,
    onSelectChange,
    refresh,
    criteria_ym,
    onMeisaiKbnChange,
  } = props

  const query = useMemo(
    () =>
      queryString.parse(window.location.search, {
        parseNumbers: true,
        parseBooleans: true,
      }),
    [window.location.search]
  )

  const path = useMemo(() => window.location.pathname, [
    window.location.pathname,
  ])

  const defaultCondition = useMemo(() => {
    return {
      criteria_ym: moment().add(-1, 'month').startOf('month').format('YYYY/MM'),
      torihikisaki_kbn: 'kumiaiin',
      meisai_kbn: 'seikyu_shiharai'
    }
  }, [])

  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [selectedIds, setSelectedIds] = useState([])
  const serverless_endpoint = getServerlessEndpoint()
  const [condition, setCondition] = useState(
    _.assign({}, defaultCondition, enableQueryCondition ? query : {})
  )

  useEffect(() => {
    setSelectedIds(
      multiple
        ? selectedId && _.isArray(selectedId)
          ? selectedId
          : []
        : selectedId
        ? [selectedId]
        : []
    )
  }, [selectedId])

  useEffect(() => {
    onSelectChange(
      selectedIds,
      _.filter(data.list, row =>
        _.find(selectedIds, id => row.torihikisaki_id === id)
      )
    )
  }, [selectedIds])

  useEffect(() => {
    setCondition(condition =>
      enableQueryCondition
        ? _(condition).assign(query).value()
        : _(condition).value()
    )
  }, [defaultCondition, enableQueryCondition, query])

  useEffect(() => {
    callApi(condition)
    setSelectedIds([])
  }, [condition, refresh])

  const handleConditionChange = (condition, isOnClear) => {
    setCondition(isOnClear ? defaultCondition : condition)
    if (enableQueryCondition)
      isOnClear
        ? history.push(`${path}`)
        : history.push(`${path}?${queryString.stringify(condition)}`)
  }

  const callApi = async condition => {
    setLoading(true)
    const { data, error } = await Axios.ajax.get(
      '/api/staff/haisha/seikyu_shiharai_meisai',
      {
        params: {
          ...condition,
        },
      }
    )
    setLoading(false)
    if (error) return
    onMeisaiKbnChange(condition.meisai_kbn)
    setData({
      list: Array.isArray(data.list) ? data.list.map(row => ({
        ...row,
        criteria_ym:condition.criteria_ym,
      })) : [],
      haisoryo_seikyu_gokei_gaku: data.haisoryo_seikyu_gokei_gaku,
      haisha_tesuryo_seikyu_gokei_gaku: data.haisha_tesuryo_seikyu_gokei_gaku,
      haisoryo_shiharai_gokei_gaku: data.haisoryo_shiharai_gokei_gaku,
    })
  }

  const getSignedUrl = async (pdf_address) => {
    const { data, error } = await Axios.ajax.get(
      `/api/meisai/download`,
      {
        baseURL: serverless_endpoint,
        params: { 
          key: pdf_address
        },
      }
    )

    if (error) {
      console.error(error)
      return null
    }

    return data.url
  }

  const fileDownload = async (pdf_address) => {
    setLoading(true)

    const signedUrl = await getSignedUrl(pdf_address)
    
    if (!signedUrl) {
      setLoading(false)
      notice({ 
        title: 'Error',
        description: 'URL取得に失敗しました',
        type: 'error',
      })
      return
    }

    const res = await Axios.ajax.get(signedUrl, { responseType: 'blob' })

    const data = res.data
    const error = res.error
    const headers = res.response.headers

    if (error){
      setLoading(false)
      notice({
        title: 'Error',
        description: 'ダウンロードに失敗しました',
        type: 'error',
      })
      return
    }

    const contentDisposition = headers['content-disposition']
    const filename = Util.extractFilenameFromContentDisposition(contentDisposition)

    setLoading(false)
    
    saveAs(data, filename ?? 'meisai.pdf')
  }

  const handleChangeSelect = (selectedId, selected) => {
    setSelectedIds(selectedIds =>
      selected
        ? multiple
          ? _.union(selectedIds, [selectedId])
          : [selectedId]
        : _.filter(selectedIds, id => id !== selectedId)
    )
  }

  const handleAllSelect = () =>
    setSelectedIds(
      _(data.list)
        .map(row => row.torihikisaki_id)
        .value()
    )

  const handleAllDeSelect = () => setSelectedIds([])

  const TableHeaderWithProps = useCallback(
    props => <TableHeader {...props} criteria_ym={criteria_ym} torihikisaki_kbn={condition.torihikisaki_kbn}/>,
    [condition.torihikisaki_kbn, criteria_ym]
  )

  const TableRowWithProps = useCallback(
    props => (
      <TableRow
        {...props}
        history={history}
        handleChangeSelect={handleChangeSelect}
        torihikisaki_kbn={condition.torihikisaki_kbn}
        fileDownload={fileDownload}
        criteria_ym={criteria_ym}
      />
    ),
    [condition.torihikisaki_kbn, history, handleChangeSelect, criteria_ym]
  )

  const SearchElementWithProps = useCallback(
    props => <SearchElement {...props} criteria_ym={criteria_ym} torihikisaki_kbn={condition.torihikisaki_kbn} meisai_kbn={condition.meisai_kbn} onMeisaiKbnChange={onMeisaiKbnChange}/>,
    [criteria_ym, condition.torihikisaki_kbn, condition.meisai_kbn, onMeisaiKbnChange]
  )

  return (
    <>
      <Segment basic loading={loading} style={{ padding: '0em' }}>
        <ConditionWrapper
          condition={condition}
          defaultCondition={defaultCondition}
          onConditionChange={handleConditionChange}
          FormElement={SearchElementWithProps}
          enableSidebar={false}
          compact
          extraContent={
            <Grid>
              <Grid.Row>
                <>
                  <Grid.Column width={4}>
                    <Segment style={{ fontWeight: 'bold', fontSize: '14px' }}>
                      <span>配送料請求額合計：</span>
                      <Text.Comma content={data.haisoryo_seikyu_gokei_gaku}/>
                    </Segment>
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <Segment style={{ fontWeight: 'bold', fontSize: '14px' }}>
                      <span>配車手数料請求額合計：</span>
                      <Text.Comma content={data.haisha_tesuryo_seikyu_gokei_gaku}/>
                    </Segment>
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <Segment style={{ fontWeight: 'bold', fontSize: '14px' }}>
                      <span>配送料支払額合計：</span>
                      <Text.Comma content={data.haisoryo_shiharai_gokei_gaku}/>
                    </Segment>
                  </Grid.Column>
                </>
              </Grid.Row>
            </Grid>
          }
        >
          <DataTable
            name={`seikyu-shiharai-meisai`}
            sortable
            filterable
            tableHeight='50vh'
            tableHeader={TableHeaderWithProps}
            tableRow={TableRowWithProps}
            data={data.list}
            topControl={
              <Button.Group>
                <Button content='全選択' onClick={handleAllSelect} />
                <Button content='全解除' onClick={handleAllDeSelect} />
              </Button.Group>
            }
            bottomControl={<></>}
            selectedIds={selectedIds}
          />
        </ConditionWrapper>
      </Segment>
    </>
  )
})

SeikyuShiharaiMeisai.propTypes = {
  enableQueryCondition: PropTypes.bool,
  multiple: PropTypes.bool,
  selectedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  onSelectChange: PropTypes.func,
  torihikisaki_kbn: PropTypes.oneOf(['kumiaiin', 'tatanso']),
  meisai_kbn: PropTypes.oneOf(['seikyu_shiharai', 'seikyu', 'shiharai']),
}

SeikyuShiharaiMeisai.defaultProps = {
  enableQueryCondition: false,
  multiple: false,
  selectedValue: '',
  onSelectChange: () => {},
  torihikisaki_kbn: 'kumiaiin',
  meisai_kbn: 'seikyu_shiharai',
}

const TableHeader = props => {
  const { withOrder, handleSort, column, direction, torihikisaki_kbn} = props

  return withOrder(
    <Table.Row>
      <Table.HeaderCell style={{ width: '3.0rem' }} content='#' />
      <Table.HeaderCell
        style={{ width: '10.0rem' }}
        sorted={column === 'unsoten_cd' ? direction : null}
        onClick={() => handleSort('unsoten_cd')}
        content={torihikisaki_kbn === 'kumiaiin' ? '組合員コード' : '単組請求支払先コード'}
      />
      {torihikisaki_kbn === 'kumiaiin' && (
        <Table.HeaderCell
          style={{ width: '10.0rem' }}
          sorted={column === 'kumiaiin_name' ? direction : null}
          onClick={() => handleSort('kumiaiin_name')}
          content='組合員名'
        />
      )}
      <Table.HeaderCell
        style={{ width: '10.0rem' }}
        sorted={column === 'unsoten_name' ? direction : null}
        onClick={() => handleSort('unsoten_name')}
        content={torihikisaki_kbn === 'kumiaiin' ? '運送店名' : '他単組名'}
      />
      <Table.HeaderCell
        style={{ width: '15.0rem' }}
        sorted={column === 'kumiai_seikyu_haisoryo_seikyu_gaku' ? direction : null}
        onClick={() => handleSort('kumiai_seikyu_haisoryo_seikyu_gaku')}
        content='組合請求（配送料請求額）'
      />
      <Table.HeaderCell
        style={{ width: '15.0rem' }}
        sorted={column === 'kumiai_seikyu_irai_tesuryo_gaku' ? direction : null}
        onClick={() => handleSort('kumiai_seikyu_irai_tesuryo_gaku')}
        content='組合請求（依頼手数料額）'
      />
      <Table.HeaderCell
        style={{ width: '15.0rem' }}
        sorted={column === 'haisoryo_seikyu_gaku' ? direction : null}
        onClick={() => handleSort('haisoryo_seikyu_gaku')}
        content='配送料請求額'
      />
      <Table.HeaderCell
        style={{ width: '10.0rem' }}
        sorted={column === 'haisoryo_nyukin_yotei_date' ? direction : null}
        onClick={() => handleSort('haisoryo_nyukin_yotei_date')}
        content='入金予定日'
      />
      <Table.HeaderCell
        style={{ width: '10.0rem' }}
        sorted={column === 'seikyu_meisai_hakko_jotai' ? direction : null}
        onClick={() => handleSort('seikyu_meisai_hakko_jotai')}
        content='請求明細発行状態'
      />
      <Table.HeaderCell
        style={{ width: '12.0rem' }}
        sorted={column === 'seikyu_meisai_saishu_hakko_datetime' ? direction : null}
        onClick={() => handleSort('seikyu_meisai_saishu_hakko_datetime')}
        content='請求明細最終発行日時'
      />
      <Table.HeaderCell
        style={{ width: '10.0rem' }}
        sorted={column === 'seikyu_meisai_pdf_address' ? direction : null}
        onClick={() => handleSort('seikyu_meisai_pdf_address')}
        content='請求明細ダウンロード'
      />
      <Table.HeaderCell
        style={{ width: '15.0rem' }}
        sorted={column === 'kumiai_seikyu_haisoryo_shiharai_gaku' ? direction : null}
        onClick={() => handleSort('kumiai_seikyu_haisoryo_shiharai_gaku')}
        content='組合請求（配送料支払額）'
      />
      <Table.HeaderCell
        style={{ width: '15.0rem' }}
        sorted={column === 'kumiai_seikyu_haisha_tesuryo_gaku' ? direction : null}
        onClick={() => handleSort('kumiai_seikyu_haisha_tesuryo_gaku')}
        content='組合請求（配車手数料額）'
      />
      <Table.HeaderCell
        style={{ width: '15.0rem' }}
        sorted={column === 'unsoten_seikyu_haisha_tesuryo_gaku' ? direction : null}
        onClick={() => handleSort('unsoten_seikyu_haisha_tesuryo_gaku')}
        content='運送店請求（配送手数料額）'
      />
      <Table.HeaderCell
        style={{ width: '15.0rem' }}
        sorted={column === 'haisoryo_shiharai_haisha_tesuryo_sosai_gaku' ? direction : null}
        onClick={() => handleSort('haisoryo_shiharai_haisha_tesuryo_sosai_gaku')}
        content='配車手数料配送料相殺額'
      />
      <Table.HeaderCell
        style={{ width: '15.0rem' }}
        sorted={column === 'haisha_tesuryo_seikyu_gaku' ? direction : null}
        onClick={() => handleSort('haisha_tesuryo_seikyu_gaku')}
        content='配車手数料請求額'
      />
      <Table.HeaderCell
        style={{ width: '15.0rem' }}
        sorted={column === 'haisoryo_shiharai_gaku' ? direction : null}
        onClick={() => handleSort('haisoryo_shiharai_gaku')}
        content='配送料支払額'
      />
      <Table.HeaderCell
        style={{ width: '10.0rem' }}
        sorted={column === 'shiharai_yotei_date' ? direction : null}
        onClick={() => handleSort('shiharai_yotei_date')}
        content='支払日'
      />
      <Table.HeaderCell
        style={{ width: '10.0rem' }}
        sorted={column === 'furikomi_kigen_date' ? direction : null}
        onClick={() => handleSort('furikomi_kigen_date')}
        content='支払期限'
      />
      <Table.HeaderCell
        style={{ width: '10.0rem' }}
        sorted={column === 'shiharai_meisai_hakko_jotai' ? direction : null}
        onClick={() => handleSort('shiharai_meisai_hakko_jotai')}
        content='支払明細発行状態'
      />
      <Table.HeaderCell
        style={{ width: '12.0rem' }}
        sorted={column === 'shiharai_meisai_saishu_hakko_datetime' ? direction : null}
        onClick={() => handleSort('shiharai_meisai_saishu_hakko_datetime')}
        content='支払明細最終発行日時'
      />
      <Table.HeaderCell
        style={{ width: '10.0rem' }}
        sorted={column === 'shiharai_meisai_pdf_address' ? direction : null}
        onClick={() => handleSort('shiharai_meisai_pdf_address')}
        content='支払明細ダウンロード'
      />
    </Table.Row>
  )
}

const TableRow = props => {
  const { withOrder, data, selectedIds, handleChangeSelect, torihikisaki_kbn, fileDownload} = props || {}

  const {
    kumiaiin_name,
    unsoten_name,
    torihikisaki_id,
    unsoten_cd,
    kumiai_seikyu_haisoryo_seikyu_gaku,
    kumiai_seikyu_irai_tesuryo_gaku,
    haisoryo_seikyu_gaku,
    haisoryo_nyukin_yotei_date,
    seikyu_meisai_hakko_jotai,
    seikyu_meisai_saishu_hakko_datetime,
    seikyu_meisai_pdf_address,
    kumiai_seikyu_haisoryo_shiharai_gaku,
    kumiai_seikyu_haisha_tesuryo_gaku,
    unsoten_seikyu_haisha_tesuryo_gaku,
    haisoryo_shiharai_haisha_tesuryo_sosai_gaku,
    haisha_tesuryo_seikyu_gaku,
    haisoryo_shiharai_gaku,
    furikomi_kigen_date,
    shiharai_yotei_date,
    shiharai_meisai_hakko_jotai,
    shiharai_meisai_saishu_hakko_datetime,
    shiharai_meisai_pdf_address,
  } = data || {}

  const [isSelected, setIsSelected] = useState(
    selectedIds.indexOf(torihikisaki_id) >= 0 ? true : false
  )

  const checkboxChange = (event, { value, checked }) => {
    event.stopPropagation()
    setIsSelected(checked)
    handleChangeSelect(value, checked)
  }

  return withOrder(
    <Table.Row
      positive={isSelected}
      onClick={() => {
        setIsSelected(state => !state)
        handleChangeSelect(torihikisaki_id, !isSelected)
      }}
    >
      <Table.Cell
        style={{ width: '3.0rem' }}
        textAlign='center'
        content={
          <Checkbox checked={isSelected} onChange={checkboxChange} value={torihikisaki_id} />
        }
      />
      <Table.Cell style={{ width: '10.0rem' }} content={unsoten_cd} />
      {torihikisaki_kbn === 'kumiaiin' && (
        <Table.Cell style={{ width: '10.0rem' }} content={kumiaiin_name} />
      )}
      <Table.Cell style={{ width: '10.0rem' }} content={unsoten_name} />
      <Table.Cell
        style={{ width: '15.0rem', textAlign: 'right' }}
        content={<Text.Comma content={kumiai_seikyu_haisoryo_seikyu_gaku === 0 ? '0' : kumiai_seikyu_haisoryo_seikyu_gaku} />}
      />
      <Table.Cell
        style={{ width: '15.0rem', textAlign: 'right' }}
        content={<Text.Comma content={kumiai_seikyu_irai_tesuryo_gaku === 0 ? '0' : kumiai_seikyu_irai_tesuryo_gaku} />}
      />
      <Table.Cell
        style={{ width: '15.0rem', textAlign: 'right' }}
        content={<Text.Comma content={haisoryo_seikyu_gaku === 0 ? '0' : haisoryo_seikyu_gaku} />}
      />
      <Table.Cell style={{ width: '10.0rem' }} content={haisoryo_nyukin_yotei_date} />
      <Table.Cell style={{ width: '10.0rem' }} content={seikyu_meisai_hakko_jotai} />
      <Table.Cell style={{ width: '12.0rem' }} content={seikyu_meisai_saishu_hakko_datetime} />
      <Table.Cell style={{ width: '10.0rem' }}>
        {seikyu_meisai_pdf_address &&(
          <Button
            primary
            content='ダウンロード'
            onClick={() => 
              fileDownload(seikyu_meisai_pdf_address)
            }
          />
        )}
      </Table.Cell>
      <Table.Cell
        style={{ width: '15.0rem', textAlign: 'right' }}
        content={<Text.Comma content={kumiai_seikyu_haisoryo_shiharai_gaku === 0 ? '0' : kumiai_seikyu_haisoryo_shiharai_gaku} />}
      />
      <Table.Cell
        style={{ width: '15.0rem', textAlign: 'right' }}
        content={<Text.Comma content={kumiai_seikyu_haisha_tesuryo_gaku === 0 ? '0' : kumiai_seikyu_haisha_tesuryo_gaku} />}
      />
      <Table.Cell
        style={{ width: '15.0rem', textAlign: 'right' }}
        content={<Text.Comma content={unsoten_seikyu_haisha_tesuryo_gaku === 0 ? '0' : unsoten_seikyu_haisha_tesuryo_gaku} />}
      />
      <Table.Cell
        style={{ width: '15.0rem', textAlign: 'right' }}
        content={<Text.Comma content={haisoryo_shiharai_haisha_tesuryo_sosai_gaku === 0 ? '0' : haisoryo_shiharai_haisha_tesuryo_sosai_gaku} />}
      />
      <Table.Cell
        style={{ width: '15.0rem', textAlign: 'right' }}
        content={<Text.Comma content={haisha_tesuryo_seikyu_gaku === 0 ? '0' : haisha_tesuryo_seikyu_gaku} />}
      />
      <Table.Cell
        style={{ width: '15.0rem', textAlign: 'right' }}
        content={<Text.Comma content={haisoryo_shiharai_gaku === 0 ? '0' : haisoryo_shiharai_gaku} />}
      />
      <Table.Cell style={{ width: '10.0rem' }} content={shiharai_yotei_date} />
      <Table.Cell style={{ width: '10.0rem' }} content={furikomi_kigen_date} />
      <Table.Cell style={{ width: '10.0rem' }} content={shiharai_meisai_hakko_jotai} />
      <Table.Cell style={{ width: '12.0rem' }} content={shiharai_meisai_saishu_hakko_datetime} />
      <Table.Cell style={{ width: '10.0rem' }}>
        {shiharai_meisai_pdf_address &&(
          <Button
            primary
            content='ダウンロード'
            onClick={() => 
              fileDownload(shiharai_meisai_pdf_address)
            }
          />
        )}
      </Table.Cell>
    </Table.Row>
  )
}

const SearchElement = props => {
  const { condition } = props

  return (
    <>
      <Grid.Column>
        <Form.Textbox.YM
          value={condition.criteria_ym}
          label='対象年月'
          name='criteria_ym'
          required
        />
      </Grid.Column>

      <Grid.Column>
        <Custom.Dropdown.UnsotenKbn
          value={condition.torihikisaki_kbn}
          name='torihikisaki_kbn'
          label='種別'
          required
        />
      </Grid.Column>

      <Grid.Column>
        <Custom.Dropdown.MeisaiKbn
          value={condition.meisai_kbn}
          name='meisai_kbn'
          label='表示する明細'
          required
        />
      </Grid.Column>
    </>
  )
}
